import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import logger from "@/utils/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock } from "lucide-react";

const AdminLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginData, {
        withCredentials: true,
      });
      // Store non-sensitive display data in sessionStorage
      sessionStorage.setItem("adminName", response.data.admin_name);
      sessionStorage.setItem("adminEmail", response.data.admin_email);
      sessionStorage.setItem("adminLoggedIn", "true");
      toast.success(`Welcome back, ${response.data.admin_name}!`);
      navigate("/admin/dashboard");
    } catch (error) {
      logger.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#3D405B] flex items-center justify-center px-6 py-20"
    >
      <Card className="w-full max-w-md bg-white border-0 shadow-2xl">
        <CardHeader className="text-center pb-0">
          <div className="flex justify-center mb-4">
            <img src="/logo.svg" alt="Djohomi Foods" className="w-16 h-16 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#3D405B] font-['Playfair_Display']">
            {t("admin.login.title")}
          </CardTitle>
          <p className="text-[#6B705C] text-sm mt-2">
            {t("admin.login.subtitle")}
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-[#3D405B] font-medium mb-2 block">
                {t("admin.login.email")}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) =>
                    setLoginData({ ...loginData, email: e.target.value })
                  }
                  placeholder={t("admin.login.emailPlaceholder")}
                  required
                  data-testid="login-email-input"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#3D405B] font-medium mb-2 block">
                {t("admin.login.password")}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) =>
                    setLoginData({ ...loginData, password: e.target.value })
                  }
                  placeholder={t("admin.login.passwordPlaceholder")}
                  required
                  data-testid="login-password-input"
                  className="pl-10"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white py-6 rounded-lg"
            >
              {loading ? t("admin.login.signingIn") : t("admin.login.signIn")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
